/**
 * ============================================================================
 * RESET PASSWORD - Password Reset Confirmation Page
 * ============================================================================
 * Purpose: Allow users to set a new password using the token from email link
 * 
 * BACKEND INTEGRATION POINTS:
 * ----------------------------------------------------------------------------
 * 🔗 Backend: POST /api/v1/auth/reset-password/:resetToken
 * Purpose: Reset user password with token (token validation happens in backend)
 * Request Body: { newPassword: string }
 * Success Response: { statusCode: 200, message: "Password reset Successfully" }
 * Error Responses:
 *   - 489: Token is invalid or expired
 *   - 400: Password validation failed
 * 
 * Note: 
 * - Token is extracted from URL path parameter (/reset-password/:token)
 * - Token validation is done by backend during password reset
 * - Frontend validates password match before sending
 * - Only confirmed password is sent to backend as "newPassword"
 * ============================================================================
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../components/Loader';

const ResetPassword = ({ isDarkMode }) => {
  const navigate = useNavigate();
  // 📌 EXTRACT TOKEN FROM URL PATH PARAMETER (/reset-password/:token)
  const { token } = useParams();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  /**
   * 📤 FORM SUBMISSION HANDLER
   * Validates password match, then submits confirmed password to backend
   * Backend validates token and resets password in single request
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage({ type: '', text: '' });

    // Validation
    const newErrors = {};
    
    if (!token) {
      setMessage({
        type: 'error',
        text: 'Invalid reset token. Please request a new password reset link.'
      });
      return;
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else {
      const passwordError = validatePassword(password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      console.log('🔄 Resetting password with token:', token);
      
      // 🌐 API CALL: Reset password with token in URL path
      // Backend validates token and resets password in single request
      // Send only the confirmed password as "newPassword"
      const API_URL = `https://taskscheduler-backend.onrender.com/api/v1/auth/reset-password/${token}`;
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          newPassword: confirmPassword  // Backend expects "newPassword"
        })
      });

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        console.log('✅ Password reset successful');
        setMessage({
          type: 'success',
          text: 'Password updated successfully! Redirecting to login...'
        });
        setPassword('');
        setConfirmPassword('');
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (result.statusCode === 489) {
        console.error('❌ Token invalid or expired:', result.message);
        setMessage({
          type: 'error',
          text: 'Your reset link is invalid or expired. Please request a new one.'
        });
      } else if (result.statusCode === 400) {
        console.error('❌ Password reset failed:', result.message);
        setMessage({
          type: 'error',
          text: result.message || 'Invalid or expired reset token. Please request a new one.'
        });
      } else {
        console.error('❌ Password reset failed:', result.message);
        setMessage({
          type: 'error',
          text: result.message || 'Failed to reset password. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      setMessage({
        type: 'error',
        text: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cardBg = isDarkMode
    ? 'linear-gradient(145deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)'
    : 'linear-gradient(145deg, #ffffff 0%, rgba(249, 250, 251, 1) 100%)';

  const inputBg = isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff';
  const inputBorder = isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.5)';
  const textColor = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#111827';
  const textSecondary = isDarkMode ? 'rgba(156, 163, 175, 0.9)' : '#6b7280';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: isDarkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: cardBg,
          borderRadius: '1.5rem',
          boxShadow: isDarkMode
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          padding: window.innerWidth <= 480 ? '2rem 1.5rem' : '3rem 2.5rem',
          border: isDarkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div
            style={{
              width: '4rem',
              height: '4rem',
              borderRadius: '1.25rem',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.2) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              border: '2px solid rgba(16, 185, 129, 0.3)'
            }}
          >
            <i className="fas fa-lock-open" style={{ fontSize: '1.75rem', color: '#10b981' }}></i>
          </div>
          <h1
            style={{
              fontSize: window.innerWidth <= 480 ? '1.75rem' : '2rem',
              fontWeight: 800,
              color: textColor,
              marginBottom: '0.5rem'
            }}
          >
            Reset Your Password
          </h1>
          <p style={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6 }}>
            Enter your new password below to complete the reset process.
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            style={{
              padding: '1rem 1.25rem',
              borderRadius: '0.85rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: message.type === 'success'
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)',
              border: message.type === 'success'
                ? '1px solid rgba(34, 197, 94, 0.3)'
                : '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <i
              className={message.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}
              style={{
                fontSize: '1.25rem',
                color: message.type === 'success' ? '#22c55e' : '#ef4444'
              }}
            ></i>
            <span style={{ color: message.type === 'success' ? '#22c55e' : '#ef4444', fontSize: '0.9rem', fontWeight: 600 }}>
              {message.text}
            </span>
          </div>
        )}

        {/* Password Reset Form */}
        {token ? (
          <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="password"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: textColor,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <i
                className="fas fa-lock"
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: textSecondary,
                  fontSize: '1rem'
                }}
              ></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 3rem',
                  borderRadius: '0.85rem',
                  border: errors.password
                    ? '2px solid #ef4444'
                    : `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: textColor,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isLoading ? 'not-allowed' : 'text',
                  opacity: isLoading ? 0.6 : 1
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: textSecondary,
                  cursor: 'pointer',
                  padding: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
            {errors.password && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fas fa-exclamation-circle"></i>
                {errors.password}
              </p>
            )}
            
            {/* Password Requirements */}
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: isDarkMode ? 'rgba(31, 41, 55, 0.3)' : 'rgba(243, 244, 246, 0.5)',
                borderRadius: '0.5rem',
                fontSize: '0.8rem'
              }}
            >
              <p style={{ color: textSecondary, marginBottom: '0.5rem', fontWeight: 600 }}>Password must contain:</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: textSecondary, lineHeight: 1.8 }}>
                <li>At least 8 characters</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
              </ul>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="confirmPassword"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: textColor,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <i
                className="fas fa-lock"
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: textSecondary,
                  fontSize: '1rem'
                }}
              ></i>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 3rem',
                  borderRadius: '0.85rem',
                  border: errors.confirmPassword
                    ? '2px solid #ef4444'
                    : `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: textColor,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isLoading ? 'not-allowed' : 'text',
                  opacity: isLoading ? 0.6 : 1
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: textSecondary,
                  cursor: 'pointer',
                  padding: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
            {errors.confirmPassword && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fas fa-exclamation-circle"></i>
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '1rem',
              borderRadius: '0.85rem',
              border: 'none',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 35px rgba(16, 185, 129, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.3)';
            }}
          >
            {isLoading ? (
              <Loader variant="inline" size="sm" text="Resetting..." isDarkMode={isDarkMode} />
            ) : (
              <>
                <i className="fas fa-check"></i>
                Reset Password
              </>
            )}
          </button>
        </form>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#ef4444', marginBottom: '1.5rem' }}>
              Invalid reset link. The URL does not contain a valid token.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '0.85rem',
                border: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 35px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(59, 130, 246, 0.3)';
              }}
            >
              <i className="fas fa-redo"></i>
              Request New Reset Link
            </button>
          </div>
        )}

        {/* Back to Login */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#10b981',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.gap = '0.75rem';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.gap = '0.5rem';
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
